/**
 * axios-ийн global interceptor (App.js) сервэрийн алдааг өөрөө мэдэгдэл болгож
 * харуулдаг:
 *
 *  - HTTP 200 боловч биедээ `statusCode` нь `ERR_STATUS_CODES`-д багтвал
 *    `message_mn` / `message_en`-ийг шууд toast болгоно;
 *  - хүсэлт бүтэлгүйтвэл (catch) ямар ч тохиолдолд toast гаргана.
 *
 * Тиймээс дуудлагын газарт дахин мэдэгдэл гаргавал ижил алдаа ХОЁР toast болж
 * давхардана — сервер тодорхой шалтгаан хэлсэн байхад зэрэгцээд ерөнхий
 * «Амжилтгүй» гарч ирдэг байсны шалтгаан нь энэ.
 *
 * Дүрэм:
 *  - `catch` дотор локал мэдэгдэл ОГТ гаргахгүй (interceptor үргэлж гаргана);
 *  - амжилтгүй хариунд `isHandledByInterceptor`-оор шалгаад, зөвхөн interceptor
 *    дуугүй өнгөрөх кодуудад л локал мэдэгдэл гаргана.
 */

/** Хариуны биед ирэхэд interceptor мэдэгдэл гаргадаг statusCode-ууд */
export const ERR_STATUS_CODES = [400, 401, 404, 500];

/**
 * Энэ хариуг interceptor аль хэдийн хэрэглэгчид мэдэгдсэн эсэх.
 *
 * @param {object} data axios хариуны бие (`response.data`)
 */
export const isHandledByInterceptor = (data) => ERR_STATUS_CODES.includes(data?.statusCode);

/** Сервер тодорхой шалтгаан хэлээгүй (эсвэл дотоод алдаа) үед харуулах мессеж */
export const GENERIC_ERROR_MESSAGE = 'Алдаа гарлаа';

/**
 * Хэрэглэгчид ХАРУУЛЖ БОЛОХГҮЙ дотоод алдааны хэв шинжүүд.
 *
 * Сервер тал дотоод алдаагаа (Mongoose-ийн валидац, JS-ийн TypeError) шууд
 * `message_mn`/`message_en`-д хийж буцаадаг тул `MerchantUser validation failed:
 * phoneNumber: Path phoneNumber (991) is shorter than the minimum allowed
 * length (8).` эсвэл `Cannot set properties of undefined (setting 'totalUnit')`
 * гэх мэт текст toast болж хэрэглэгчид харагддаг байв. Өгөгдлийн загварын нэр,
 * талбарын зам, дүрмийн утга нь хэрэглэгчид ямар ч хэрэггүй бөгөөд системийн
 * дотоод бүтцийг ил гаргадаг.
 */
const TECHNICAL_MESSAGE_PATTERNS = [
  /validation failed/i,
  /cannot (?:read|set) propert/i,
  /is not (?:a function|defined)/i,
  /\bundefined\b/i,
  /\bnull\b/i,
  /Path\s+[`'"]?\w+[`'"]?\s+\(/i,
  /(?:shorter|longer) than the (?:minimum|maximum) allowed length/i,
  /\b(?:ValidationError|CastError|MongoError|MongoServerError|TypeError|ReferenceError)\b/,
  /\bE11000\b/,
  /\bObjectId\b/,
  /\bat\s+\S+\.js:\d+/,
];

/** Кирилл үсэг агуулсан эсэх — хэрэглэгчид зориулсан мессеж үргэлж кириллээр */
const hasCyrillic = (text) => /[Ѐ-ӿ]/.test(text);

/**
 * Серверийн мессеж хэрэглэгчид харуулахад тохиромжтой эсэх.
 *
 * Кирилл үсэг огт байхгүй бол энэ нь хөгжүүлэгчид зориулсан англи (эсвэл
 * дотоод) текст — хэрэглэгчид уншиж ойлгох зүйл байхгүй. Кирилл байсан ч
 * дотоод алдааны хэв шинж агуулж байвал мөн нуугдана.
 */
export const isUserFacingMessage = (message) => {
  if (typeof message !== 'string') return false;

  const text = message.trim();
  if (text === '') return false;
  if (!hasCyrillic(text)) return false;

  return !TECHNICAL_MESSAGE_PATTERNS.some((pattern) => pattern.test(text));
};

/**
 * Хариунаас хэрэглэгчид харуулах мессежийг гаргана.
 *
 * Сервер тодорхой, монгол шалтгаан хэлсэн бол түүнийг хэвээр харуулна — эс
 * бөгөөс дотоод техникийн текст оронд ерөнхий мессеж буцаана. Ингэснээр
 * хэрэглэгч «Алдаа гарлаа» гэж л харах бөгөөд дотоод бүтэц ил гарахгүй.
 *
 * @param {object} data axios хариуны бие (`response.data`)
 * @returns {string} toast-д харуулах мессеж
 */
export const toUserMessage = (data) => {
  const candidates = [data?.message_mn, data?.errorMessageMn, data?.message_en, data?.message];

  return candidates.find(isUserFacingMessage) ?? GENERIC_ERROR_MESSAGE;
};
